import { GENDERS } from 'components/intake/PatientInfoSection';
import { STATE_OPTIONS } from 'constants/states';

import { DEMOGRAPHIC_FIELDS, ReferralFieldDef, VALUE_NAMES } from '../constants';
import { ssnError } from '../ssn';

// The fields the sync modal reviews (OLIO-2846) — the referral's own demographics, plus State, which
// the vendor needs and working the referral never does.
//
// DELIBERATELY NOT ADDED TO DEMOGRAPHIC_FIELDS. That list is the referral entry/edit form, a shipped
// screen; State is a fact needed to push a patient into an EHR, not a fact about the referral.
// Composing here keeps it off that form while guaranteeing the two lists can't drift on the fields
// they share.
//
// The MEDICAL RECORD NUMBER used to live here too. It moved to the platform (OLIO-2846 follow-up):
// whether a facility needs one is facility configuration, not something the coordinator pushing a
// patient can know, so it is recorded once per facility in the patient-sync settings page and the
// platform generates it. Nothing on this screen asks for it any more.
export const SYNC_STATE_FIELD: ReferralFieldDef = {
  name: VALUE_NAMES.PATIENT_STATE,
  label: 'State',
  type: 'select',
};

export const MATRIXCARE = 'matrixcare';

// PCC's schema has no State element — the platform's mapper carries none for PCC — so showing it for
// a PCC client would ask for data that goes nowhere.
export function syncFields(platform: string | null): ReferralFieldDef[] {
  if (platform !== MATRIXCARE) return DEMOGRAPHIC_FIELDS;
  return [...DEMOGRAPHIC_FIELDS, SYNC_STATE_FIELD];
}

// Every `select` field needs its list, or it renders an empty dropdown nothing can be picked from —
// which is what Sex did. GENDERS is the same list the referral form and /patients/new use.
export function optionsForField(name: string) {
  if (name === VALUE_NAMES.PATIENT_STATE) return STATE_OPTIONS;
  if (name === VALUE_NAMES.PATIENT_GENDER) return GENDERS;
  return undefined;
}

// Everything that must hold before Send is offered, keyed by field name so each error renders against
// its own control. Mirrors olio-api's DispatchGuard and the platform's field_schema so the client
// never offers a Send the server will refuse — the coordinator finds out here, with the field in front
// of them, rather than from a 422.
export function syncFieldErrors(values: Record<string, string>, platform: string | null): Record<string, string> {
  const errors: Record<string, string> = {};

  // Both vendors require these four.
  for (const field of DEMOGRAPHIC_FIELDS) {
    if (!field.optional && !values[field.name]?.trim()) errors[field.name] = `${field.label} is required to push.`;
  }

  // SSN stays optional — but a PRESENT one must be a real number, because a redacted value is
  // invisible once masked and would become a permanent vendor matching key.
  const ssn = ssnError(values[VALUE_NAMES.PATIENT_SSN]);
  if (ssn) errors[VALUE_NAMES.PATIENT_SSN] = ssn;

  if (platform === MATRIXCARE) {
    if (!values[VALUE_NAMES.PATIENT_STATE]?.trim()) {
      errors[VALUE_NAMES.PATIENT_STATE] = 'State is required to push a patient to MatrixCare.';
    }

    // MatrixCare's guide p13: at least one field BEYOND the minimum set (first/last/sex/DOB). On a
    // referral push there are only two ways to satisfy it — the rule also accepts address fragments,
    // but this modal renders no address block, so offering them would be advice nobody can act on.
    //
    // Checked here because the platform is otherwise the ONLY place it is enforced: without this the
    // coordinator clicks Send, waits out a full async round trip, and learns about it from a rejection
    // card. It fires more often than it sounds like it should — extraction produces `patient_ssn` but
    // never `patient_middle_name`, so any packet without an SSN lands here.
    //
    // Attached to MIDDLE NAME deliberately, even though either field satisfies it. An SSN becomes a
    // permanent matching key at MatrixCare — it is why a redacted one is refused outright — so the
    // highlighted remedy should be the harmless field, with the SSN named as the alternative rather
    // than the default. Presence is the test, matching the platform: a present-but-invalid SSN is
    // already blocked by ssnError above, and clearing it correctly surfaces this instead.
    const hasMiddleName = !!values[VALUE_NAMES.PATIENT_MIDDLE_NAME]?.trim();
    const hasSsn = !!values[VALUE_NAMES.PATIENT_SSN]?.trim();
    if (!hasMiddleName && !hasSsn) {
      errors[VALUE_NAMES.PATIENT_MIDDLE_NAME] =
        'MatrixCare additionally requires either a Middle Name OR Social Security Number';
    }
  }

  return errors;
}
